import React, { useState } from "react";
import ReactDOM from "react-dom";
import styles from "./Tooltip.module.css";

interface TooltipProps {
    text: string;
    children: React.ReactNode;
    variant?: 'default' | 'green'; // Variant prop for different color schemes
    position?: 'top' | 'bottom' | 'left' | 'right';
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, variant = 'default', position = 'right' }) => {
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    const showTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const gap = 2; // Gap between element and tooltip

        let top = 0;
        let left = 0;

        switch (position) {
            case 'top':
                top = rect.top - gap;
                left = rect.left + rect.width / 2;
                break;
            case 'bottom':
                top = rect.bottom + gap;
                left = rect.left + rect.width / 2;
                break;
            case 'left':
                top = rect.top + rect.height / 2;
                left = rect.left - gap;
                break;
            case 'right':
            default:
                top = rect.top + rect.height / 2;
                left = rect.right + gap;
                break;
        }

        setCoords({ top, left });
    };

    const hideTooltip = () => setCoords(null);

    // Determine CSS classes based on variant
    const tooltipClass = variant === 'green'
        ? `${styles.tooltip} ${styles.tooltipGreen}`
        : `${styles.tooltip} ${styles.tooltipDefault}`;

    const arrowClass = variant === 'green'
        ? `${styles.arrow} ${styles.arrowGreen}`
        : `${styles.arrow} ${styles.arrowDefault}`;

    const positionClass = position === 'top'
        ? `${styles.top}`
        : position === 'bottom'
            ? `${styles.bottom}`
            : position === 'left'
                ? `${styles.left}`
                : `${styles.right}`;

    return (
        <>
            <div onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
                {children}
            </div>
            {coords &&
                ReactDOM.createPortal(
                    <div
                        className={`${tooltipClass} ${positionClass}`}
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {text}
                        <span className={arrowClass}></span>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;